/**
 * Redux slice — Tasks V2 Migration v4 plan PR8.
 *
 * Backs the ExecuteResultsBrowserPanel + ExecuteProgressStrip. Hits
 * the BE endpoint introduced in PR5:
 *
 *     GET /api/v1/management/tasks-migration/runs/{run_id}/results/
 *
 * Query params:
 *   limit, offset                                 -- pagination
 *   filter_outcome = CREATED | ERRORED | SKIPPED
 *   filter_has_drift = true                       -- run.summary verify failures
 *   filter_reminder_id = <int>                    -- FE drill-down
 *
 * 404 from the endpoint means ``TASKS_V2_MIGRATION_PREVIEW_V2`` is OFF
 * on the BE -- we set ``featureAvailable=false`` and the panels render
 * null. Same convention as PR7's tasksV2DryRunPreview.
 *
 * Two consumers:
 *   * ExecuteResultsBrowserPanel: full pagination, filter chips, etc.
 *   * ExecuteProgressStrip: polls during in-flight runs, only reads
 *     the most recent N rows. Uses ``ttailRows`` buffer separate from
 *     the main ``rows`` so polling doesn't fight pagination.
 */
import { createSlice } from '@reduxjs/toolkit';

import { dispatch } from '../store';
import { callAdminApi } from '../../utils/apiCall';


const BASE = '/api/v1/management/tasks-migration';


const initialState = {
    byRun: {},
    featureAvailable: true,
};


function _emptyRunBuffer() {
    return {
        rows: [],
        count: 0,
        offset: 0,
        limit: 25,
        filters: {
            outcome: null,
            has_drift: false,
            reminder_id: null,
        },
        loading: false,
        error: null,
        // Live-progress polling buffer -- separate so the polling
        // component doesn't clobber the operator's paginated view.
        tailRows: [],
        tailLoading: false,
        tailLastFetchAt: null,
    };
}


const slice = createSlice({
    name: 'tasksV2ExecuteResults',
    initialState,
    reducers: {
        setLoading(state, action) {
            const { runId, loading } = action.payload;
            if (!state.byRun[runId]) state.byRun[runId] = _emptyRunBuffer();
            state.byRun[runId].loading = loading;
        },
        setRows(state, action) {
            const { runId, rows, count, offset, limit, filters } = action.payload;
            if (!state.byRun[runId]) state.byRun[runId] = _emptyRunBuffer();
            const buf = state.byRun[runId];
            buf.rows = rows;
            buf.count = count;
            buf.offset = offset;
            buf.limit = limit;
            if (filters) buf.filters = filters;
            buf.error = null;
        },
        setError(state, action) {
            const { runId, error } = action.payload;
            if (!state.byRun[runId]) state.byRun[runId] = _emptyRunBuffer();
            state.byRun[runId].error = error;
        },
        setTailLoading(state, action) {
            const { runId, loading } = action.payload;
            if (!state.byRun[runId]) state.byRun[runId] = _emptyRunBuffer();
            state.byRun[runId].tailLoading = loading;
        },
        setTailRows(state, action) {
            const { runId, rows, count } = action.payload;
            if (!state.byRun[runId]) state.byRun[runId] = _emptyRunBuffer();
            const buf = state.byRun[runId];
            buf.tailRows = rows;
            // ``count`` is the run's TOTAL produced count (server returns
            // full count regardless of limit), used for the progress bar.
            if (typeof count === 'number') buf.count = count;
            buf.tailLastFetchAt = Date.now();
        },
        setFeatureUnavailable(state) {
            state.featureAvailable = false;
        },
        clearForRun(state, action) {
            const runId = action.payload;
            delete state.byRun[runId];
        },
        clearAll(state) {
            state.byRun = {};
            state.featureAvailable = true;
        },
    },
});


export default slice.reducer;
export const { clearForRun, clearAll } = slice.actions;


/**
 * Fetch a paginated page of execute result rows for the browser panel.
 */
export function fetchExecuteResultsPage(runId, opts = {}) {
    return async () => {
        if (!runId) return null;

        const limit = opts.limit ?? 25;
        const offset = opts.offset ?? 0;
        const params = new URLSearchParams();
        params.set('limit', String(limit));
        params.set('offset', String(offset));
        if (opts.filter_outcome) {
            params.set('filter_outcome', opts.filter_outcome);
        }
        if (opts.filter_has_drift) {
            params.set('filter_has_drift', 'true');
        }
        if (opts.filter_reminder_id != null) {
            params.set('filter_reminder_id', String(opts.filter_reminder_id));
        }

        dispatch(slice.actions.setLoading({ runId, loading: true }));
        try {
            const url = `${BASE}/runs/${runId}/results/?${params.toString()}`;
            const response = await callAdminApi(url);
            if (response.status === 404) {
                dispatch(slice.actions.setFeatureUnavailable());
                return null;
            }
            if (response.status !== 200) {
                dispatch(slice.actions.setError({
                    runId, error: `HTTP ${response.status}`,
                }));
                return null;
            }
            const data = response.data || {};
            dispatch(slice.actions.setRows({
                runId,
                rows: Array.isArray(data.results) ? data.results : [],
                count: typeof data.count === 'number' ? data.count : 0,
                offset, limit,
                filters: {
                    outcome: opts.filter_outcome || null,
                    has_drift: !!opts.filter_has_drift,
                    reminder_id: opts.filter_reminder_id ?? null,
                },
            }));
            return data;
        } catch (error) {
            dispatch(slice.actions.setError({
                runId, error: error?.message || String(error),
            }));
            return null;
        } finally {
            dispatch(slice.actions.setLoading({ runId, loading: false }));
        }
    };
}


/**
 * Fetch the tail (most-recent N rows) for the live-progress strip.
 *
 * This is a separate path from ``fetchExecuteResultsPage`` so the
 * polling loop doesn't fight the operator's paginated view. The tail
 * always fetches ``offset=0, limit=10`` and writes to ``tailRows``.
 */
export function fetchExecuteResultsTail(runId, { limit = 10 } = {}) {
    return async () => {
        if (!runId) return null;
        dispatch(slice.actions.setTailLoading({ runId, loading: true }));
        try {
            const params = new URLSearchParams();
            params.set('limit', String(limit));
            params.set('offset', '0');
            const url = `${BASE}/runs/${runId}/results/?${params.toString()}`;
            const response = await callAdminApi(url);
            if (response.status === 404) {
                dispatch(slice.actions.setFeatureUnavailable());
                return null;
            }
            if (response.status !== 200) {
                return null;
            }
            const data = response.data || {};
            dispatch(slice.actions.setTailRows({
                runId,
                rows: Array.isArray(data.results) ? data.results : [],
                count: typeof data.count === 'number' ? data.count : null,
            }));
            return data;
        } catch (_e) {
            return null;
        } finally {
            dispatch(slice.actions.setTailLoading({ runId, loading: false }));
        }
    };
}
